import React, {
  memo,
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  ButtonIcon,
  Chip,
  FooterActionBar,
  Icon,
  type IconKind,
  Menu,
  type MenuOption,
  Tooltip,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  ALLOWED_ACTIONS,
  CF_CRT_MED,
  CF_NEU_INV,
  CF_WRN_STL,
  LIST_ACTIONS,
  TABS_FILTERS,
  VIOLATIONS_DETAILS_TABS,
  VIOLATIONS_TAB_LIST_ACTIONS,
  VIOLATIONS_TAB_LIST_FOOTER_KIND,
  violationsHome,
} from '../../../../shared/constants';
import { useInvertedLocalize, useNavigateTo } from '../../../../shared/hooks';
import { detailsActions } from '../../../Details';
import { type ViolationsTabSidePanelRowContextState } from '../../data-layer/violationsTabTypes';
import { violationsTabActions } from '../../data-store/violationsTabSlice';

import './ViolationsListActions.css';

/**
 * Props interface for ViolationsListActionsWrapper component
 * @interface
 */
interface ViolationsListActionsWrapperProps {
  children: ReactNode;
  kind: string | undefined;
  className: string;
}

/**
 * A wrapper component that renders violation list actions either in a footer or standard layout
 *
 * @param {ViolationsListActionsWrapperProps} props - Component props
 * @param {ReactNode} props.children - Child elements to be rendered
 * @param {VIOLATIONS_TAB_LIST_FOOTER_KIND} props.kind - Determines if the actions should be rendered in footer style
 * @returns {JSX.Element} Rendered component
 */
function ViolationsListActionsWrapper({
  children,
  kind,
  className,
}: ViolationsListActionsWrapperProps): JSX.Element {
  const footerActionClasses = cx(
    'Sod-ViolationsListActions--isFooter',
    className
  );
  const listActionClasses = cx('Sod-ViolationsListActions', className);

  if (kind === VIOLATIONS_TAB_LIST_FOOTER_KIND.FOOTER) {
    return (
      <FooterActionBar className={footerActionClasses}>
        <div />
        <Box className='Sod-ViolationsListActions-footerWrapper'>
          {children}
        </Box>
      </FooterActionBar>
    );
  }

  return <Box className={listActionClasses}>{children}</Box>;
}

interface ViolationsListActionsProps {
  kind?: string;
  tabInAction: string | null;
  isDisabled: boolean;
  rowContext: ViolationsTabSidePanelRowContextState;
  className?: string | null;
}

/**
 * ViolationsListActions
 */
function ViolationsListActions({
  kind,
  tabInAction,
  isDisabled,
  rowContext,
  className,
}: ViolationsListActionsProps): ReactElement | null {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { navigateToDetails } = useNavigateTo();

  const [optionsWithIcons, setOptionsWithIcons] = useState([] as MenuOption[]);

  const isFooterKind = useMemo(
    () => Boolean(kind === VIOLATIONS_TAB_LIST_FOOTER_KIND.FOOTER),
    [kind]
  );
  const isMitigatedTab = useMemo(
    () => Boolean(tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value),
    [tabInAction]
  );
  const isReadOnlyAction = useMemo(
    () => Boolean(rowContext.isReadOnlyActions),
    [rowContext.isReadOnlyActions]
  );
  const actionsList = useMemo(() => {
    const { allowedActions } = rowContext;

    if (!allowedActions) {
      return [];
    }

    const tempList: string[] = [];

    allowedActions.forEach((action) => {
      if (action in ALLOWED_ACTIONS) {
        tempList.push(
          ...ALLOWED_ACTIONS[action as keyof typeof ALLOWED_ACTIONS]
        );
      }
    });

    return tempList;
  }, [rowContext]);
  const getOptionDisabledStatus = useCallback(
    (value: string) => {
      if (isDisabled) {
        return true;
      }

      if (isReadOnlyAction) {
        return !(
          value === LIST_ACTIONS.VIEWDETAILS.value ||
          value === LIST_ACTIONS.GENERATEREPORT.value
        );
      }

      return !actionsList.includes(value);
    },
    [actionsList, isDisabled, isReadOnlyAction]
  );

  // Populate the list actions
  useEffect(() => {
    if (tabInAction === null) {
      return;
    }

    const originalList = VIOLATIONS_TAB_LIST_ACTIONS[tabInAction];

    // Setting up actions
    const isMitigateCase = Boolean(
      tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value &&
        Boolean(rowContext.mitigatingControlCount) &&
        Number(rowContext.mitigatingControlCount) > 1
    );
    const isOpenCase = Boolean(tabInAction === TABS_FILTERS.OPENCOUNT.value);

    // Case: Mitigated tab, not in review, count > 1
    if (isMitigateCase) {
      const updatedOptions = originalList.map((o) => {
        const isLabelWithCount = Boolean(
          o.value === LIST_ACTIONS.REMOVEMC.value ||
            o.value === LIST_ACTIONS.CHNAGEMCENDDATE.value
        );
        const isOptionDisabled = getOptionDisabledStatus(o.value);

        if (isLabelWithCount) {
          return {
            ...o,
            label: l(`${o.label} ({count})`, {
              count: rowContext.mitigatingControlCount ?? 0,
            }),
            isDisabled: isOptionDisabled,
          };
        }

        return {
          ...o,
          label: l(o.label),
          isDisabled: isOptionDisabled,
        };
      });

      if (isFooterKind) {
        setOptionsWithIcons(
          updatedOptions.filter((o) => o.value !== LIST_ACTIONS.REMEDIATE.value)
        );

        return;
      }

      setOptionsWithIcons(updatedOptions);

      return;
    }

    if (isOpenCase) {
      const mitigateActionRemoved = originalList.filter(
        (o) => o.value !== LIST_ACTIONS.MITIGATE.value
      );

      setOptionsWithIcons(
        mitigateActionRemoved.map((o) => {
          const isOptionDisabled = getOptionDisabledStatus(o.value);

          return {
            ...o,
            label: l(o.label),
            isDisabled: isOptionDisabled,
          };
        })
      );

      return;
    }

    // Default case
    setOptionsWithIcons(
      originalList.map((o) => {
        const isOptionDisabled = Boolean(getOptionDisabledStatus(o.value));

        return {
          ...o,
          label: l(o.label),
          isDisabled: isOptionDisabled,
        };
      })
    );
  }, [
    l,
    tabInAction,
    rowContext,
    isReadOnlyAction,
    isFooterKind,
    isDisabled,
    actionsList,
    getOptionDisabledStatus,
  ]);

  const handleOptionClick = useCallback(
    (option: { value: string }) => {
      const manager = {
        isOpen: true,
        sodKey: rowContext.id,
        action: option.value,
        title: `${rowContext.title ?? ''} (${rowContext.username ?? ''})`,
        userName: rowContext.username,
        isReadOnlyActions: rowContext.isReadOnlyActions,
        allowedActions: rowContext.allowedActions,
        displayName: rowContext.displayName,
        mitigatingControlCount: rowContext.mitigatingControlCount,
        riskId: rowContext.riskId,
        rulesetName: rowContext.rulesetName,
        functions: rowContext.functions,
        accounts: rowContext.accounts,
        startDate: rowContext.startDate,
        endDate: rowContext.endDate,
      };
      const redirectProps = {
        key: rowContext.id,
        params: {
          title: `${rowContext.title ?? ''}`,
          user: `${rowContext.username ?? ''}`,
          type: violationsHome,
          activeTab:
            tabInAction === TABS_FILTERS.CLOSEDCOUNT.value
              ? VIOLATIONS_DETAILS_TABS.TAB_SUMMARY
              : VIOLATIONS_DETAILS_TABS.TAB_CONFLICTS,
        },
      };

      if (option.value === LIST_ACTIONS.VIEWDETAILS.value) {
        // Sharing action context with details page
        dispatch(
          detailsActions.setViolationTabdetailsActionManager({
            ...manager,
            isOpen: false,
          })
        );
        navigateToDetails(redirectProps);

        return;
      }

      dispatch(
        violationsTabActions.setViolationsActionManager({
          ...manager,
          redirectProps,
        })
      );
    },
    [dispatch, tabInAction, navigateToDetails, rowContext]
  );

  const wrapperClasses = cx(
    optionsWithIcons.length === 0 && 'Sod-ViolationsListActions--isEmpty',
    isMitigatedTab && 'Sod-ViolationsListActions--isMitigatedTab',
    className
  );

  const getExpireIcon = useCallback(() => {
    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiredCount &&
      rowContext.isExpiredCount > 0
    ) {
      return <Icon kind='AlertCriticalFilled' color={CF_CRT_MED} />;
    }

    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiringSoonCount &&
      rowContext.isExpiringSoonCount > 0
    ) {
      return <Icon kind='AlertWarningFilled' color={CF_WRN_STL} />;
    }

    return null;
  }, [
    rowContext.isExpiredCount,
    rowContext.isExpiringSoonCount,
    rowContext.mitigatingControlCount,
  ]);

  const getToolTipMessage = useCallback(() => {
    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiredCount &&
      rowContext.isExpiredCount > 1
    ) {
      return l('{count} of the {totalCount} mitigating controls have expired', {
        count: rowContext.isExpiredCount,
        totalCount: rowContext.mitigatingControlCount,
      });
    }

    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiredCount &&
      rowContext.isExpiredCount > 0
    ) {
      return l('{count} of the {totalCount} mitigating controls has expired', {
        count: rowContext.isExpiredCount,
        totalCount: rowContext.mitigatingControlCount,
      });
    }

    if (
      rowContext.mitigatingControlCount &&
      rowContext.isExpiringSoonCount &&
      rowContext.isExpiringSoonCount > 0
    ) {
      return l('{count} of the {totalCount} mitigating controls expires soon', {
        count: rowContext.isExpiringSoonCount,
        totalCount: rowContext.mitigatingControlCount,
      });
    }

    return undefined;
  }, [
    l,
    rowContext.isExpiredCount,
    rowContext.isExpiringSoonCount,
    rowContext.mitigatingControlCount,
  ]);

  const renderListPrimaryButton = useCallback(() => {
    if (tabInAction === TABS_FILTERS.MITIGATEDCOUNT.value) {
      return (
        <Box
          tag='div'
          className='Sod-ViolationsListActions-primaryBtnWrapper--isMitigatedTab'>
          <Tooltip
            trigger={
              <>
                <Chip
                  kind='subtleFilled'
                  label={l(
                    rowContext.mitigatingControlCount &&
                      rowContext.mitigatingControlCount > 1
                      ? `Mitigated (${rowContext.mitigatingControlCount})`
                      : 'Mitigated'
                  )}
                  color='info'
                  size='small'
                  isInteractionDisabled
                />
                {getExpireIcon()}
              </>
            }
            text={getToolTipMessage()}
            isPortalEnabled
            placement='bottom'
          />
        </Box>
      );
    }

    if (tabInAction === TABS_FILTERS.OPENCOUNT.value) {
      return (
        <Button
          type='button'
          kind='subtle'
          size='medium'
          isDisabled={getOptionDisabledStatus(LIST_ACTIONS.MITIGATE.value)}
          onClick={() => {
            handleOptionClick({ value: LIST_ACTIONS.MITIGATE.value });
          }}>
          {l('Mitigate')}
        </Button>
      );
    }

    return null;
  }, [
    l,
    tabInAction,
    rowContext.mitigatingControlCount,
    getExpireIcon,
    getToolTipMessage,
    handleOptionClick,
    getOptionDisabledStatus,
  ]);

  const renderFooterPrimaryButton = useCallback(() => {
    if (tabInAction === TABS_FILTERS.OPENCOUNT.value) {
      return (
        <Button
          type='button'
          kind='filled'
          size='medium'
          leftIcon={<Icon kind='SodMitigate' color={CF_NEU_INV} />}
          isDisabled={getOptionDisabledStatus(LIST_ACTIONS.MITIGATE.value)}
          onClick={() => {
            handleOptionClick({
              value: LIST_ACTIONS.MITIGATE.value,
            });
          }}>
          {l('Mitigate')}
        </Button>
      );
    }

    if (optionsWithIcons.length > 0) {
      const firstAction = optionsWithIcons[0];

      return (
        <Button
          type='button'
          kind='outlined'
          size='medium'
          leftIcon={
            <Icon kind={firstAction.icon as IconKind} color={CF_NEU_INV} />
          }
          isDisabled={getOptionDisabledStatus(firstAction.value)}
          onClick={() => {
            handleOptionClick({
              value: firstAction.value,
            });
          }}>
          {firstAction.label}
        </Button>
      );
    }

    return null;
  }, [
    l,
    tabInAction,
    optionsWithIcons,
    handleOptionClick,
    getOptionDisabledStatus,
  ]);

  if (tabInAction === null) {
    return null;
  }

  const renderOptions =
    isFooterKind && tabInAction !== TABS_FILTERS.OPENCOUNT.value
      ? optionsWithIcons.slice(1)
      : optionsWithIcons;

  return (
    <ViolationsListActionsWrapper kind={kind} className={wrapperClasses}>
      {isFooterKind ? renderFooterPrimaryButton() : renderListPrimaryButton()}
      {optionsWithIcons.length > 0 && (
        <Menu
          placement='bottom-end'
          options={renderOptions}
          onChange={handleOptionClick}
          minOptionsForSearch={optionsWithIcons.length + 1}
          trigger={
            <ButtonIcon
              size='medium'
              kind='ghost'
              ariaLabel={l('More actions')}
              icon={<Icon kind='MoreVertical' />}
            />
          }
          isPortalEnabled
        />
      )}
    </ViolationsListActionsWrapper>
  );
}

ViolationsListActions.defaultProps = {
  kind: VIOLATIONS_TAB_LIST_FOOTER_KIND.LIST,
  className: null,
};

export default memo(ViolationsListActions);
